'use client'

import React from 'react'
import { Annotation, Annotation2 } from '@/types/annotation'
import { AnimatedMarkdown3D } from '@/components/messages/animated-markdown-3d'

// Utility: prefer wrapped .result if present
function unwrap<T = any>(maybeWrapped: any): T | undefined {
  if (!maybeWrapped) return undefined
  return typeof maybeWrapped === 'object' && 'result' in maybeWrapped
    ? (maybeWrapped as any).result
    : maybeWrapped
}

const exts = {
  model: ['.glb', '.gltf'],
  ply: ['.ply'],
  video: ['.mp4', '.webm', '.mov'],
  image: ['.png', '.jpg', '.jpeg', '.webp']
}

function findUrls(obj: any): string[] {
  const results: string[] = []
  const visit = (val: any) => {
    if (!val) return
    if (typeof val === 'string') {
      const s = val.toLowerCase()
      if (s.startsWith('http://') || s.startsWith('https://')) {
        results.push(val)
      }
    } else if (Array.isArray(val)) {
      val.forEach(visit)
    } else if (typeof val === 'object') {
      Object.values(val).forEach(visit)
    }
  }
  visit(obj)
  return Array.from(new Set(results))
}

function filterByExt(urls: string[], list: string[]) {
  return urls.filter((u) => {
    const s = u.toLowerCase()
    return list.some((ext) => s.includes(ext))
  })
}

export function ThreeDAssetAnnotation({ annotation }: { annotation: Annotation | Annotation2 }) {
  const raw = (annotation as any)['threeDAssetGenerator__generate3DAsset']
  if (!raw)
    return (
      <div className="flex flex-col gap-3">
        <div className="h-48 w-full rounded-md bg-muted/30 motion-safe:animate-pulse motion-reduce:animate-none" />
        <div className="space-y-2">
          <div className="h-4 w-5/6 rounded bg-muted/40 motion-safe:animate-pulse motion-reduce:animate-none" />
          <div className="h-4 w-4/6 rounded bg-muted/30 motion-safe:animate-pulse motion-reduce:animate-none" />
        </div>
      </div>
    )

  const result = unwrap<any>(raw)
  if (!result)
    return (
      <div className="flex flex-col gap-3">
        <div className="h-48 w-full rounded-md bg-muted/30 motion-safe:animate-pulse motion-reduce:animate-none" />
        <div className="space-y-2">
          <div className="h-4 w-5/6 rounded bg-muted/40 motion-safe:animate-pulse motion-reduce:animate-none" />
          <div className="h-4 w-4/6 rounded bg-muted/30 motion-safe:animate-pulse motion-reduce:animate-none" />
        </div>
      </div>
    )

  const allUrls = findUrls(result)
  const modelUrls = filterByExt(allUrls, exts.model)
  const plyUrls = filterByExt(allUrls, exts.ply)
  const videoUrls = filterByExt(allUrls, exts.video)
  const imageUrls = filterByExt(allUrls, exts.image)

  const primary3D = modelUrls[0] || plyUrls[0]

  const hasAny =
    !!primary3D ||
    videoUrls.length > 0 ||
    imageUrls.length > 0 ||
    modelUrls.length + plyUrls.length > 0

  return (
    <div className="flex flex-col gap-4">
      {!hasAny && (
        <div className="flex flex-col gap-3">
          <div className="h-48 w-full rounded-md bg-muted/30 motion-safe:animate-pulse motion-reduce:animate-none" />
          <div className="space-y-2">
            <div className="h-4 w-5/6 rounded bg-muted/40 motion-safe:animate-pulse motion-reduce:animate-none" />
            <div className="h-4 w-4/6 rounded bg-muted/30 motion-safe:animate-pulse motion-reduce:animate-none" />
          </div>
        </div>
      )}
      {primary3D && (
        <AnimatedMarkdown3D src={primary3D} alt="Generated 3D Asset" isStreaming={false} />
      )}

      {/* Other assets list */}
      {(videoUrls.length > 0 || imageUrls.length > 0 || modelUrls.length + plyUrls.length > 1) && (
        <div className="rounded-md border bg-muted/20 p-3">
          <div className="mb-2 text-sm font-medium">Related Outputs</div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {/* Additional 3D files */}
            {[...modelUrls.slice(1), ...plyUrls.slice(primary3D ? 1 : 0)].map((u) => (
              <a
                key={u}
                href={u}
                target="_blank"
                rel="noreferrer"
                className="truncate text-sm text-primary hover:underline"
              >
                {u}
              </a>
            ))}
            {/* Videos */}
            {videoUrls.map((u) => (
              <a
                key={u}
                href={u}
                target="_blank"
                rel="noreferrer"
                className="truncate text-sm text-primary hover:underline"
              >
                {u}
              </a>
            ))}
            {/* Images */}
            {imageUrls.map((u) => (
              <a
                key={u}
                href={u}
                target="_blank"
                rel="noreferrer"
                className="truncate text-sm text-primary hover:underline"
              >
                {u}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default ThreeDAssetAnnotation
